import type { CSSProperties } from 'react';
import { getPerson } from '../lifeos/api.js';
import Sheet from '../lifeos/Sheet.js';
import { sphereColor, sphereName } from '../lifeos/spheres.js';
import { useLoad } from '../lifeos/useLoad.js';
import type { Project, Sphere } from '../lifeos/types.js';
import type { RoleBy } from '../lib/projectJobs.js';
import Icon from '../ui/Icon.js';
import { projectTree } from './JobTabs.js';

/** What a message is about: a sphere (job or personal), optionally one of its projects. */
export interface Role {
  sphereKey: string;
  projectId: string | null;
}

export const PERSONAL = 'personal';

/** 'Personal', "Ferguson's Garage Doors", or the workspace name. */
export function roleLabel(role: { sphereKey: string | null; projectId: string | null }, spheres: readonly Sphere[], projects: readonly Project[]): string {
  if (role.projectId) {
    const p = projects.find((x) => x.id === role.projectId);
    if (p) return p.name;
  }
  return sphereName(spheres as Sphere[], role.sphereKey ?? PERSONAL);
}

/** "Set by you" vs a guess, worded for the chip's title and the sheet. */
export function roleByWord(by: RoleBy | undefined): string {
  if (by === 'owner') return 'Set by you';
  if (by === 'model') return 'Claude’s guess';
  if (by === 'rule') return 'Set by a rule';
  return 'Not set';
}

/**
 * The small role chip: "Personal" / "Ferguson's Garage Doors", in the
 * sphere's colour. A guess shows "auto"; the owner's own choice shows a tick.
 * Interactive (a button) only where it is not inside another tap target.
 */
export function RoleChip({
  role,
  roleBy,
  spheres,
  projects,
  onClick,
}: {
  role: { sphereKey: string | null; projectId: string | null };
  roleBy?: RoleBy;
  spheres: readonly Sphere[];
  projects: readonly Project[];
  onClick?: () => void;
}): JSX.Element {
  const label = roleLabel(role, spheres, projects);
  const style = { '--job': sphereColor(spheres as Sphere[], role.sphereKey ?? PERSONAL) } as CSSProperties;
  const inner = (
    <>
      <span className="role-chip__label">{label}</span>
      {roleBy === 'owner' ? (
        <Icon name="check" size={12} className="role-chip__by" />
      ) : roleBy === 'model' || roleBy === 'rule' ? (
        <span className="role-chip__by role-chip__by--auto">auto</span>
      ) : null}
    </>
  );
  if (!onClick) {
    return (
      <span className="role-chip" style={style} title={roleByWord(roleBy)}>
        {inner}
      </span>
    );
  }
  return (
    <button type="button" className="role-chip role-chip--tap" style={style} title={roleByWord(roleBy)} aria-label={`About: ${label} (${roleByWord(roleBy)}). Change`} onClick={onClick}>
      {inner}
      <Icon name="chevron-right" size={12} className="role-chip__chev" />
    </button>
  );
}

/**
 * Pick what a message is about: Personal and the sender's own projects first
 * (one tap), then every workspace and project.
 */
export function RolePickerSheet({
  current,
  contactId,
  spheres,
  projects,
  onPick,
  onClose,
}: {
  current: Role | null;
  contactId: number | null;
  spheres: readonly Sphere[];
  projects: readonly Project[];
  onPick: (role: Role) => void;
  onClose: () => void;
}): JSX.Element {
  const person = useLoad(() => (contactId ? getPerson(contactId).catch(() => null) : Promise.resolve(null)), [contactId]);
  const live = projects.filter((p) => p.status !== 'archived' && p.status !== 'done');
  const theirs = (person.data?.projects ?? []).map((r) => live.find((p) => p.id === r.id)).filter((p): p is Project => Boolean(p));
  const theirSpheres = [...new Set([person.data?.person.sphereKey, ...theirs.map((p) => p.sphereKey)].filter((k): k is string => Boolean(k) && k !== PERSONAL))];
  const same = (r: Role): boolean => current !== null && current.sphereKey === r.sphereKey && current.projectId === r.projectId;
  const opt = (key: string, r: Role, label: string, sub?: string, indent = 0): JSX.Element => (
    <li key={key} className="ui-swipe">
      <button type="button" role="option" aria-selected={same(r)} className="ui-pick" style={indent ? { paddingLeft: 16 + indent * 20 } : undefined} onClick={() => onPick(r)}>
        <span className="ui-dot" style={{ background: sphereColor(spheres as Sphere[], r.sphereKey) }} aria-hidden="true" />
        <span className="ui-pick__label">
          {label}
          {sub && <span className="role-pick__sub"> · {sub}</span>}
        </span>
        {same(r) && <Icon name="check" size={20} className="ui-pick__check" />}
      </button>
    </li>
  );
  const others = spheres.filter((s) => s.key !== PERSONAL);
  return (
    <Sheet title="What is this about?" onClose={onClose}>
      <p className="person__label">Quick</p>
      <ul className="ui-group ui-group--flush" role="listbox" aria-label="Suggested">
        {opt('personal', { sphereKey: PERSONAL, projectId: null }, 'Personal')}
        {theirSpheres.map((k) => opt(`s:${k}`, { sphereKey: k, projectId: null }, sphereName(spheres as Sphere[], k), 'work'))}
        {theirs.map((p) => opt(`tp:${p.id}`, { sphereKey: p.sphereKey, projectId: p.id }, p.name))}
      </ul>
      <p className="person__label">Everything</p>
      <ul className="ui-group ui-group--flush" role="listbox" aria-label="All workspaces and projects">
        {others.flatMap((s) => [
          opt(`a:${s.key}`, { sphereKey: s.key, projectId: null }, s.name),
          ...projectTree(live.filter((p) => p.sphereKey === s.key)).map(({ project: p, depth }) =>
            opt(`ap:${p.id}`, { sphereKey: p.sphereKey, projectId: p.id }, p.name, undefined, depth + 1),
          ),
        ])}
      </ul>
    </Sheet>
  );
}
